import React, { useEffect, useState } from "react";
import { FiArrowLeft, FiMapPin, FiPlus } from "react-icons/fi";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { getAuth } from "../../services/localStorage";
import { API_ENDPOINT } from "../../services/api";
import Labels from "../../services/apiLabels";
import {
  EMPTY_FORM,
  authHeaders,
  errorText,
  fieldErrorsFromApi,
  isApiSuccess,
  isValidCoord,
  listFromPayload,
  officeId,
  officePayload,
  readJson,
  toForm,
} from "./officeHelpers";

const LIST_PATH = "/dealers/dashbord/offices";

function OfficeForm() {
  const history = useHistory();
  const location = useLocation();
  const { id } = useParams();
  const editingId = id || null;
  const auth = getAuth();

  const [form, setForm] = useState(
    location.state?.office ? toForm(location.state.office) : EMPTY_FORM
  );
  const [errors, setErrors] = useState({});
  const [banner, setBanner] = useState({ type: "", text: "" });
  const [saving, setSaving] = useState(false);
  const [loadingOffice, setLoadingOffice] = useState(Boolean(editingId));
  const [locating, setLocating] = useState(false);

  useEffect(() => {
    if (!editingId) {
      setForm(EMPTY_FORM);
      setLoadingOffice(false);
      return undefined;
    }
    if (location.state?.office && String(officeId(location.state.office)) === String(editingId)) {
      setForm(toForm(location.state.office));
      setLoadingOffice(false);
      return undefined;
    }
    if (!auth?.token) {
      setLoadingOffice(false);
      setBanner({ type: "error", text: "Please sign in again." });
      return undefined;
    }

    let cancelled = false;
    setLoadingOffice(true);
    fetch(API_ENDPOINT(Labels.office_listing), {
      headers: authHeaders(auth.token),
    })
      .then((response) => readJson(response).then((json) => ({ response, json })))
      .then(({ response, json }) => {
        if (cancelled) return;
        if (!isApiSuccess(response, json)) throw new Error("load failed");
        const match = listFromPayload(json).find(
          (office) => String(officeId(office)) === String(editingId)
        );
        if (!match) {
          setBanner({ type: "error", text: "This office could not be found." });
          return;
        }
        setForm(toForm(match));
      })
      .catch(() => {
        if (!cancelled) {
          setBanner({
            type: "error",
            text: "Could not load this office. Please try again.",
          });
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingOffice(false);
      });

    return () => {
      cancelled = true;
    };
  }, [auth?.token, editingId, location.state]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: undefined }));
    if (banner.text) setBanner({ type: "", text: "" });
  };

  const goToList = (message) => {
    history.push({
      pathname: LIST_PATH,
      state: message ? { notice: message } : undefined,
    });
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) {
      setBanner({
        type: "error",
        text: "Location is not available in this browser.",
      });
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setForm((prev) => ({
          ...prev,
          latitude: String(pos.coords.latitude.toFixed(6)),
          longitude: String(pos.coords.longitude.toFixed(6)),
        }));
        setErrors((prev) => ({
          ...prev,
          latitude: undefined,
          longitude: undefined,
        }));
        setLocating(false);
      },
      () => {
        setLocating(false);
        setBanner({
          type: "error",
          text: "Could not read your location. Enter latitude and longitude manually.",
        });
      },
      { enableHighAccuracy: true, timeout: 12000 }
    );
  };

  const validate = () => {
    const next = {};
    if (!String(form.Address).trim()) next.Address = "Address is required";
    if (!String(form.Postcode).trim()) next.Postcode = "Postcode is required";
    if (!String(form.Area).trim()) next.Area = "Area is required";
    if (!isValidCoord(form.latitude, -90, 90)) {
      next.latitude = "Enter a latitude between -90 and 90";
    }
    if (!isValidCoord(form.longitude, -180, 180)) {
      next.longitude = "Enter a longitude between -180 and 180";
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    if (!auth?.token) {
      setBanner({ type: "error", text: "Please sign in again." });
      return;
    }

    setSaving(true);
    setBanner({ type: "", text: "" });
    try {
      const isEdit = editingId != null;
      const path = isEdit
        ? `${Labels.update_office}${editingId}/`
        : Labels.create_office;
      const response = await fetch(API_ENDPOINT(path), {
        method: isEdit ? "PATCH" : "POST",
        headers: authHeaders(auth.token, true),
        body: JSON.stringify(officePayload(form)),
      });
      const json = await readJson(response);
      if (!isApiSuccess(response, json)) {
        const apiFields = fieldErrorsFromApi(json);
        if (Object.keys(apiFields).length) {
          setErrors((prev) => ({ ...prev, ...apiFields }));
          return;
        }
        throw new Error(
          errorText(
            json,
            `Could not ${isEdit ? "update" : "create"} the office.`
          )
        );
      }
      goToList(isEdit ? "Office updated." : "Office added.");
    } catch (err) {
      setBanner({
        type: "error",
        text: err?.message || "Could not save the office. Please try again.",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="dd-section dd-offices">
      <form className="dd-form dd-offices__form" onSubmit={handleSubmit} noValidate>
        <div className="dd-arb-intro">
          <span className="dd-arb-intro__badge" aria-hidden>
            <FiMapPin size={18} />
          </span>
          <div>
            <h2>{editingId ? "Edit office" : "Add office"}</h2>
            <p>
              {editingId
                ? "Update this inspection centre’s address and map position."
                : "Add an inspection centre with address, area, postcode, and map position."}
            </p>
          </div>
        </div>

        {banner.text ? (
          <p
            className={
              banner.type === "success"
                ? "dd-password__hint"
                : "dd-password__error"
            }
            role="status"
          >
            {banner.text}
          </p>
        ) : null}

        {loadingOffice ? (
          <div className="dd-empty">
            <strong>Loading office…</strong>
          </div>
        ) : (
          <>
            <div className="dd-form-grid">
              <div
                className={`dd-field dd-field--full${
                  errors.Address ? " is-invalid" : ""
                }`}
              >
                <label htmlFor="office-address">Address *</label>
                <input
                  id="office-address"
                  name="Address"
                  className="form-control"
                  value={form.Address}
                  onChange={handleChange}
                  placeholder="Street and building"
                  autoComplete="street-address"
                />
                {errors.Address ? (
                  <p className="dd-password__error">{errors.Address}</p>
                ) : null}
              </div>

              <div className={`dd-field${errors.Area ? " is-invalid" : ""}`}>
                <label htmlFor="office-area">Area *</label>
                <input
                  id="office-area"
                  name="Area"
                  className="form-control"
                  value={form.Area}
                  onChange={handleChange}
                  placeholder="Town or area"
                  autoComplete="address-level2"
                />
                {errors.Area ? (
                  <p className="dd-password__error">{errors.Area}</p>
                ) : null}
              </div>

              <div className={`dd-field${errors.Postcode ? " is-invalid" : ""}`}>
                <label htmlFor="office-postcode">Postcode *</label>
                <input
                  id="office-postcode"
                  name="Postcode"
                  className="form-control"
                  value={form.Postcode}
                  onChange={handleChange}
                  placeholder="e.g. D02 XY00"
                  autoComplete="postal-code"
                />
                {errors.Postcode ? (
                  <p className="dd-password__error">{errors.Postcode}</p>
                ) : null}
              </div>

              <div className={`dd-field${errors.latitude ? " is-invalid" : ""}`}>
                <label htmlFor="office-lat">Latitude *</label>
                <input
                  id="office-lat"
                  name="latitude"
                  className="form-control"
                  value={form.latitude}
                  onChange={handleChange}
                  placeholder="53.349805"
                  inputMode="decimal"
                />
                {errors.latitude ? (
                  <p className="dd-password__error">{errors.latitude}</p>
                ) : null}
              </div>

              <div className={`dd-field${errors.longitude ? " is-invalid" : ""}`}>
                <label htmlFor="office-lng">Longitude *</label>
                <input
                  id="office-lng"
                  name="longitude"
                  className="form-control"
                  value={form.longitude}
                  onChange={handleChange}
                  placeholder="-6.26031"
                  inputMode="decimal"
                />
                {errors.longitude ? (
                  <p className="dd-password__error">{errors.longitude}</p>
                ) : null}
              </div>
            </div>

            <div className="dd-btn-row">
              <button
                type="button"
                className="dd-btn dd-btn--ghost"
                onClick={() => goToList()}
                disabled={saving}
              >
                <FiArrowLeft size={16} aria-hidden />
                Back to list
              </button>
              <button
                type="button"
                className="dd-btn dd-btn--ghost"
                onClick={useMyLocation}
                disabled={locating || saving}
              >
                {locating ? "Locating…" : "Use my location"}
              </button>
              <button
                type="submit"
                className="dd-btn dd-btn--primary"
                disabled={saving || loadingOffice}
              >
                <FiPlus size={16} aria-hidden />
                {saving
                  ? "Saving…"
                  : editingId
                    ? "Update office"
                    : "Add office"}
              </button>
            </div>
          </>
        )}
      </form>
    </div>
  );
}

export default OfficeForm;
